import Vue from 'vue'
import Cart from 'lib/cart'
import state from 'lib/appState'
import { formatPrice, isSubscriptionVariant, hasSubscriptionOption, getPerUnitPrice, uniqueNumber, handleize } from 'lib/util'
import setQuery from 'set-query-string'
import queryString from 'query-string'
import {
  PURCHASE_TYPE_SUBSCRIPTION,
  PURCHASE_TYPE_ONETIME
} from 'lib/constants'
import scrollMonitor from 'scrollmonitor'
import 'modules/tooltip/tooltip.vue.js'
import 'modules/product-description/product-description.vue.js'
import 'modules/affix/affix'
import 'modules/content-drawer/content-drawer.vue'
import bus from 'lib/bus'
import select from 'select-dom'
import { scrollToSelector } from 'lib/scrolls'

export default el => {
  return new Vue({
    el,
    data () {
      const product = window.HU.product
      const currentQuery = queryString.parse(window.location.search)

      let preSelected = null
      if (currentQuery.variant) {
        preSelected = product.variants.find(({ id }) => Number(currentQuery.variant) === Number(id))
      }

      const selected = product['options_with_values'].reduce((obj, option) => {
        obj[option.name] = preSelected ? preSelected[`option${option.position}`] : option.values[0]
        return obj
      }, {})
      state.selectedOptions = selected

      const defaultPurchaseType = hasSubscriptionOption(product) ? PURCHASE_TYPE_SUBSCRIPTION : PURCHASE_TYPE_ONETIME

      return {
        busy: false,
        subscriptionDropdown: false,
        state,
        product,
        purchaseType: currentQuery.purchase_type ? currentQuery.purchase_type : defaultPurchaseType,
        subscriptionFrequency: currentQuery.interval ? currentQuery.interval : null,
        quantity: 1,
        hideReviewText: true,
        isProductLoading: false
      }
    },
    watch: {
      'state.selectedOptions': {
        handler () {
          this.pickActiveVariant()
        },
        deep: true
      },
      'state.activeVariant': function () {
        this.updateQueryParams()
        this.$nextTick(() => {
          const bis = document.getElementById('bis')
          const btn = bis.querySelector('.btn')
          if (btn) {
            if (!this.state.activeVariant.available) {
              btn.removeAttribute('style')
            } else {
              btn.style.display = 'none'
            }
          }
        })
      },
      purchaseType () {
        this.updateQueryParams()
      },
      subscriptionFrequency () {
        this.updateQueryParams()
      }
    },
    created () {
      this.maybeScrollToReviews()
      this.pickActiveVariant()
      if (!this.hasSubscriptionOption) return null
      if (this.subscriptionFrequency === null) {
        this.subscriptionFrequency = this.frequencyIntervals[0]
      }
    },
    mounted () {
      this.atcWatcher = scrollMonitor.create(this.$refs.atcWrapper)
      this.atcWatcher.enterViewport(this.onATCEnterView)
      this.atcWatcher.exitViewport(this.onATCExitView)
      bus.$on('product-sticky-nav/ADD_TO_CART', this.addToCart)
      this.$nextTick(() => {
        this.initBIS()
      })
    },
    computed: {
      productFlavors () {
        if (Object.keys(this.product).length && this.product.flavors) {
          return this.product.flavors
        }
        return []
      },
      getOptionsFlavor () {
        if (this.productFlavors.length) {
          const optionsWithValues = this.productFlavors.reduce((result, product) => {
            const matchedItem = product.options_with_values.find(item => item.name === 'Flavor')
            result.push({
              name: matchedItem.name,
              options: {
                label: matchedItem.values[0],
                value: product.handle
              }
            })
            return result
          }, [])
          const result = Array.from(new Set(optionsWithValues.map(k => k.name))).map(name => {
            return {
              name: name,
              options: optionsWithValues.filter(s => s.name === name).map(edition => edition.options)
            }
          })
          return result
        }
        return []
      },
      bundleIdentifier () {
        return this.product.tags.reduce((match, tag) => {
          if (match) return match
          if (tag.includes('BI:')) return tag.replace('BI:', '')
          return match
        }, null)
      },
      compareAtPrice () {
        if (this.activeVariant.compare_at_price) {
          return `$${formatPrice(this.activeVariant.compare_at_price, 2)}`
        }
      },
      hasBundleValue () {
        if (this.product.tags) {
          return this.product.tags.some(tag => tag === 'bundle-base-product' || tag.includes('BI:'))
        }
      },
      subscriptionSelected () {
        return this.purchaseType === PURCHASE_TYPE_SUBSCRIPTION
      },
      activeVariant () {
        return this.state.activeVariant
      },
      subscriptionData () {
        const {
          subscription = null
        } = this.product

        if (!subscription || !subscription.subscription_id) return null
        return {
          is_subscription: true,
          original_variant_id: this.state.activeVariant.id,
          shipping_interval_frequency: this.subscriptionFrequency,
          shipping_interval_unit_type: this.frequenctIntervalUnit,
          subscription_id: subscription.subscription_id,
          discount_percentage: parseFloat(subscription.discount_percentage)
        }
      },
      activeSubscriptionPrice () {
        const { subscriptions = {} } = this.activeVariant
        const price = parseFloat(subscriptions['discount_variant_price'])
        if (isNaN(price)) return 0
        return price * 100 // return in cents always for consistency
      },
      frequencyIntervals () {
        return this.product.subscription.shipping_interval_frequency.split(',')
      },
      frequenctIntervalUnit () {
        return this.product.subscription.shipping_interval_unit_type
      },
      hasSubscriptionOption () {
        return !this.isOnPreorder && hasSubscriptionOption(this.product)
      },
      perUnitPrice () {
        return getPerUnitPrice(this.activeVariant)
      },
      bundleVariants () {
        return window.HU.bundle_variants ? window.HU.bundle_variants : []
      },
      discountPercent () {
        if (!this.activeVariant.compare_at_price) return 0
        const discount = this.activeVariant.compare_at_price - this.activeVariant.price
        return discount / this.activeVariant.compare_at_price * 100
      },
      isOnPreorder () {
        return this.product.tags.includes('PREORDER')
      },
      preorderCartNote () {
        return this.product.tags.reduce((message, tag) => {
          if (message) return message
          if (tag.includes('PREORDER_CART_NOTE')) {
            return tag.replace('PREORDER_CART_NOTE::', '')
          }
          return null
        }, null)
      },
      baseProductProps () {
        const base = {}
        if (this.isOnPreorder) {
          base['_preorderItem'] = true

          if (this.preorderCartNote) {
            base['_preorderCartNote'] = this.preorderCartNote
          }
        }
        return base
      },
      pricePerUnit () {
        if (!this.activeVariant || isNaN(this.activeVariant.pack_size) || !this.activeVariant.pack_size) {
          return
        }

        return `$${formatPrice(this.activeVariant.price / this.activeVariant.pack_size, 2)}/${this.activeVariant.pack_size_unit ? this.activeVariant.pack_size_unit : 'unit'}`
      },
      subscriptionPricePerUnit () {
        if (!this.activeVariant || isNaN(this.activeVariant.pack_size) || !this.activeVariant.pack_size) {
          return
        }

        return `$${formatPrice(this.activeSubscriptionPrice / this.activeVariant.pack_size, 2)}/${this.activeVariant.pack_size_unit ? this.activeVariant.pack_size_unit : 'unit'}`
      },
      currentPrice () {
        if (!this.activeVariant) {
          return
        }

        if (this.hasSubscriptionOption && this.subscriptionSelected && this.subscriptionData) {
          return `$${formatPrice(this.activeSubscriptionPrice, 2)}`
        }

        return `$${formatPrice(this.activeVariant.price, 2)}`
      },
      submitButtonText () {
        if (!this.activeVariant) {
          return 'Add to Cart'
        }

        if (this.activeVariant.available) {
          return 'Add to Cart'
        } else {
          return 'Sold out Join The Waitlist'
        }
      },
      selected () {
        return this.state.selectedOptions
      },
      quantityOptionPosition () {
        const quantity = this.product.options_with_values.find(position => handleize(position.name) === 'quantity')
        return quantity ? quantity.position : 0
      },
      flavorOptionPosition () {
        const flavor = this.product.options_with_values.find(position => handleize(position.name) === 'flavor')
        return flavor ? flavor.position : 0
      }
    },
    methods: {
      handleize,
      clicktFirstVariant () {
        const selectedVariant = select('.js-options-quantity')
        if (selectedVariant) {
          selectedVariant.click()
        }
      },
      showFrequencyDropdown () {
        this.subscriptionDropdown = !this.subscriptionDropdown
      },
      frequencySelect (value) {
        this.subscriptionFrequency = value
        this.subscriptionDropdown = !this.subscriptionDropdown
      },
      toggleDrawer (target) {
        this.state.activeDrawer = target
        this.state.modalToggleEl = document.activeElement
      },
      onATCEnterView (event, props) {
        this.state.showATCSticky = false
      },
      onATCExitView (event, props) {
        if (props.isBelowViewport) return
        this.state.showATCSticky = true
      },
      updateQueryParams () {
        setQuery({
          variant: this.state.activeVariant ? this.state.activeVariant.id : '',
          purchase_type: this.purchaseType,
          interval: this.subscriptionFrequency
        })
      },
      pickActiveVariant () {
        const {
          variants = [],
          options = []
        } = this.product

        for (let i = 0; i < variants.length; i++) {
          const variant = variants[i]
          const isMatch = options.every((option, _i) => {
            return (
              variant[`option${_i + 1}`] === this.state.selectedOptions[option]
            )
          })
          if (isMatch) {
            state.activeVariant = variant
            break
          }
        }
      },
      updateSelectedOption (name, value) {
        this.clicktFirstVariant()
        Vue.set(this.state.selectedOptions, name, value)
      },
      addFreeGift () {
        const freeGiftVariantId = state.freeGiftVariantId
        const cartItems = state.cart.items
        const freeGiftTotalGoal = state.cart.total_price
        const freeGiftGoal = state.freeGiftGoal
        const freeGiftType = state.freeGiftType

        if (freeGiftVariantId > 0 && freeGiftType === 'goal') {
          const result = cartItems.filter(product => {
            return product.id === freeGiftVariantId
          })

          if (result.length < 1 && freeGiftTotalGoal >= freeGiftGoal) {
            let payload = [{
              id: freeGiftVariantId,
              quantity: this.quantity
            }]
            Cart.add(payload)
          }
        }
      },
      async addToCart () {
        try {
          const variant = this.state.activeVariant
          this.busy = true
          const baseProperties = this.baseProductProps
          if (this.hasSubscriptionOption && this.subscriptionSelected && this.subscriptionData) {
            const { shipping_interval_frequency, shipping_interval_unit_type, subscription_id, discount_percentage, original_variant_id, is_subscription } = this.subscriptionData
            const discoutnVariantID = this.activeVariant.subscriptions ? this.activeVariant.subscriptions['discount_variant_id'] : this.activeVariant.id
            const handle = this.product.handle
            await Cart.addSubscription(discoutnVariantID, this.quantity, shipping_interval_frequency, shipping_interval_unit_type, subscription_id, { handle,  is_subscription, original_variant_id, discount_percentage, ...baseProperties })
          } else {
            let payload = [{
              id: variant.id,
              quantity: this.quantity,
              properties: {
                ...baseProperties
              }
            }]
            if (this.subscriptionData) {
              const { shipping_interval_frequency, shipping_interval_unit_type, subscription_id, discount_percentage } = this.subscriptionData
              let subscriptionPrice = variant.price - (variant.price * Number(discount_percentage) / 100)
              payload = [{
                id: variant.id,
                quantity: this.quantity,
                properties: {
                  _discount_percentage: discount_percentage,
                  _shipping_interval_unit_type: shipping_interval_unit_type,
                  _shipping_interval_frequency: shipping_interval_frequency,
                  _subscription_key: subscription_id,
                  _subscription_price: subscriptionPrice,
                  _subscription_variant_id: variant.subscriptions.discount_variant_id,
                  _frequencyIntervals: this.frequencyIntervals,
                  ...baseProperties
                }
              }]
            }
            if (this.product.type.includes('Bundle') && this.bundleVariants.length) {
              const bundleID = uniqueNumber()
              payload = this.bundleVariants.map((id) => {
                return {
                  id,
                  quantity: this.quantity,
                  properties: {
                    ...baseProperties,
                    'For Bundle': this.product.title,
                    '_bundleID': bundleID,
                    '_BI': this.bundleIdentifier,
                    '_bundleProductID': this.product.variants[0].id
                  }
                }
              })

              payload.push({
                id: this.product.variants[0].id,
                quantity: 1,
                properties: {
                  _hide: true
                }
              })
            }
            await Cart.add(payload)
          }
          this.addFreeGift()
          try {
            // window.location = '/cart'
            window.MicroBus.emit(`side-cart:toggle`)
          } catch (error) {
            console.log(error)
            state.isMiniCartOpen = true
          }
        } catch (error) {
          console.log(error)
        } finally {
          this.busy = false
          bus.$emit('product-form/ADDDED_TO_CART')
        }
      },
      fetchVariantWith (matchingOption, pos) {
        const { variants = [], options = [] } = this.product
        const matchOption = `option${pos}`
        return variants.find((variant) => {
          return options.every((option, i) => {
            if (`option${i + 1}` === matchOption) {
              return variant[matchOption] === matchingOption
            }
            return variant[`option${i + 1}`] === this.state.selectedOptions[option]
          })
        })
      },
      getDurationFor (option, pos) {
        const currentVariant = this.fetchVariantWith(option, pos)
        if (isSubscriptionVariant(currentVariant) && currentVariant.savings && currentVariant.savings.product_sub_savings) {
          return 'Delivered Monthly'
        }
        return 'One Time Purchase'
      },
      getPriceFor (option, pos) {
        const currentVariant = this.fetchVariantWith(option, pos)
        if (currentVariant.compare_at_price) {
          return `<del>$${formatPrice(currentVariant.compare_at_price, 2)}</del><ins>$${formatPrice(currentVariant.price, 2)}</ins>`
        }
        return `$${formatPrice(currentVariant.price, 2)}`
      },
      getSavingsFor (option, pos) {
        const currentVariant = this.fetchVariantWith(option, pos)
        if (isSubscriptionVariant(currentVariant) && currentVariant.savings && currentVariant.savings.product_sub_savings) {
          return `You save ${currentVariant.savings.product_sub_savings}`
        }
        return null
      },
      loadFlavor (obj) {
        if (!obj) return
        this.isProductLoading = true
        setTimeout(() => {
          const productHandle = obj.value
          const productFlavors = this.productFlavors.find(item => item.handle === productHandle)
          const data = Object.assign({}, this.product, productFlavors)
          this.product = data
          this.state.activeProduct = data
          const selected = this.product['options_with_values'].reduce((obj, option) => {
            obj[option.name] = option.values[0]
            return obj
          }, {})
          this.state.selectedOptions = selected
          this.pickActiveVariant()
          this.maybeDisableVariantOption()
          window.history.replaceState(null, null, `/products/${data.handle}`)
          this.isProductLoading = false
          this.updateYotpo()
        }, 300)
      },
      getFlavorOptions (options) {
        return function () {
          return options
        }
      },
      getFlavorOptionValue (value) {
        return function () {
          return value
        }
      },
      updateYotpo () {
        if (!window.Yotpo) {
          const api = new window.Yotpo.API(yotpo)
          api.refreshWidgets()
        }
      },
      onFlavorChange (obj) {
        this.isProductLoading = true
        setTimeout(() => {
          const sizeVariantsOptions = this.product.variants.filter(v => v[`option${this.flavorOptionPosition}`] === obj.value).map(v => v[`option${this.quantityOptionPosition}`])
          if (!sizeVariantsOptions.includes(this.selected['Quantity'])) {
            this.updateSelectedOption('Quantity', sizeVariantsOptions[0])
          }
          this.updateSelectedOption(obj.name, obj.value)
          this.isProductLoading = false
        }, 300)
      },
      getVariantOptions (name) {
        const product = this.product

        return function () {
          if (!product[name]) {
            return []
          }
          return product[name].map(value => {
            return {
              value,
              label: value
            }
          })
        }
      },
      getOptionValue (name) {
        const selectOptions = this.selected
        return function () {
          if (selectOptions && selectOptions[name]) {
            return selectOptions[name]
          }
        }
      },
      maybeDisableVariantOption (value, position) {
        const variant = this.fetchVariantWith(value, position)

        return Boolean(
          !variant ||
          (variant.inventory_quantity <= 0 && !variant.available)
        )
      },
      getQuantityOptions () {
        return [
          {label: '1', value: '1'},
          {label: '2', value: '2'},
          {label: '3', value: '3'},
          {label: '4', value: '4'},
          {label: '5', value: '5'}
        ]
      },
      getInitialQuantityValue () {
        return this.quantity
      },
      onQuantityChange ({name, value}) {
        this.quantity = parseInt(value, 10)
      },
      initBIS () {
        klaviyo.init(window.__PRODUCT_BIS_SETTINGS__.initSettings)
        klaviyo.enable('backinstock', window.__PRODUCT_BIS_SETTINGS__)
      },
      maybeScrollToReviews () {
        const reviewsSelector = window.location.hash
        if (reviewsSelector === '#hu-yotpo-reviews') {
          setTimeout(() => {
            scrollToSelector(reviewsSelector)
          }, 1000)
        }
      }
    }
  })
}
